import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import CreateAdGroupTab from "./components/CreateAdGroupTab";
import SavedAdGroupsTab from "./components/SavedAdGroupsTab";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { MediaType } from "../../CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/types";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import {
  getAdGroupTemplates,
  setAdGroupTemplate,
  setCampaignBasicDetails,
  setSelectedAdGroupTemplate,
} from "../../../redux/reducers/campaignToCreate";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

const PanelRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const TabBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
}));

const TabGroup = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: theme.palette.brand.gray100,
}));

const MediaTypeSection = styled("div")({
  display: "flex",
  flexDirection: "column" as const,
  gap: 6,
});

const MediaTypeLabel = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.gray500,
}));

const ToggleGroup = styled("div")({
  display: "flex",
  gap: 8,
});

const ToggleButton = styled("button")(({ theme }: { theme: Theme }) => ({
  padding: "8px 20px",
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  background: theme.palette.brand.white,
  color: theme.palette.brand.gray500,
  transition: "background 0.15s, color 0.15s, border-color 0.15s",
  "&[data-active='true']": {
    background: theme.palette.brand.coral,
    color: theme.palette.brand.white,
    borderColor: theme.palette.brand.coral,
  },
  "&:hover:not([data-active='true'])": {
    borderColor: theme.palette.brand.gray400,
    color: theme.palette.brand.gray700,
  },
}));

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  textAlign: "center" as const,
  padding: "32px 0",
}));

const TabButton = styled("button")(({ theme }: { theme: Theme }) => ({
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: theme.palette.brand.gray500,
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 16px",
  borderRadius: 8,
  transition: "all 0.15s ease",
  "&[data-active='true']": {
    background: theme.palette.brand.white,
    color: theme.palette.brand.gray900,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
}));

type AdGroupSelectionPanelProps = {
  panelMode?: boolean;
  onSelectionChange?: (selectedId: string | null) => void;
};

const AdGroupSelectionPanel: FC<AdGroupSelectionPanelProps> = ({ panelMode, onSelectionChange }) => {
  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const { selectedAdvertiser } = useGlobalSelectedAdvertiser();
  const basicDetails = useSelector((state: ReduxApplicationState) => state.campaignToCreate.basicDetails);
  const dispatch = useDispatch();

  const mediaType: MediaType = basicDetails.isVideoCampaign ? "video" : "display";

  const handleMediaTypeChange = (type: MediaType) => {
    dispatch(setCampaignBasicDetails({ ...basicDetails, isVideoCampaign: type === "video" }));
  };

  const handleTabChange = (tab: 0 | 1) => {
    setActiveTab(tab);
    dispatch(setAdGroupTemplate(tab === 0));
  };

  const handleCreated = (templateId: string) => {
    if (selectedAdvertiser) {
      dispatch(getAdGroupTemplates(selectedAdvertiser.brandgenAdvertiserId));
    }
    dispatch(setSelectedAdGroupTemplate(templateId));
    dispatch(setAdGroupTemplate(true));
    setActiveTab(0);
  };

  return (
    <PanelRoot>
      {panelMode && (
        <ManageLink>
          To create or manage Ad Groups, go to{" "}
          <a href="/ads-upload" target="_blank" rel="noopener noreferrer">
            Ad Groups ↗
          </a>
          .
        </ManageLink>
      )}

      {!selectedAdvertiser && <AdvertiserPrompt>Please select an advertiser from the profile menu.</AdvertiserPrompt>}

      {selectedAdvertiser && (
        <>
          <TabBar>
            <TabGroup>
              <TabButton type="button" data-active={String(activeTab === 0)} onClick={() => handleTabChange(0)}>
                Existing Ad Groups
              </TabButton>
              <TabButton type="button" data-active={String(activeTab === 1)} onClick={() => handleTabChange(1)}>
                Create Ad Group
              </TabButton>
            </TabGroup>
            <MediaTypeSection>
              <MediaTypeLabel>Media Type</MediaTypeLabel>
              <ToggleGroup>
                <ToggleButton
                  type="button"
                  data-active={String(mediaType === "display")}
                  onClick={() => handleMediaTypeChange("display")}
                >
                  Display
                </ToggleButton>
                <ToggleButton
                  type="button"
                  data-active={String(mediaType === "video")}
                  onClick={() => handleMediaTypeChange("video")}
                >
                  Video
                </ToggleButton>
              </ToggleGroup>
            </MediaTypeSection>
          </TabBar>

          {activeTab === 0 ? (
            <SavedAdGroupsTab
              selectedAdvertiser={selectedAdvertiser}
              isVideoCampaign={mediaType === "video"}
              onSelectionChange={onSelectionChange}
              panelMode={panelMode}
            />
          ) : (
            <CreateAdGroupTab
              selectedAdvertiser={selectedAdvertiser}
              isVideoCampaign={mediaType === "video"}
              onCreated={handleCreated}
            />
          )}
        </>
      )}
    </PanelRoot>
  );
};

export default AdGroupSelectionPanel;
